import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import {
  getActiveDepositScheme,
  getBrandConfig,
} from '@awazevr/mfe-common.configs.brand'
import { formatPropertyPageURL as properFormatPropertyPageURL } from '@awazevr/mfe-common.utils.pdp-url'
import {
  AccommodationType,
  SearchCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'
import { decode } from 'html-entities'
import addDays from 'date-fns/addDays'
import { AwazeCountry } from 'src/utils/globalProps'
import { oneSearchCall } from './one-search'
import { SearchOptions, SupportedBrand } from './properties.types'
import { IPropertyCard } from '@awazevr/design-system.compositions.property-card/dist/property-card.types'
import { convertLinkToAbsoluteUrl } from '../../utils/helpers/helpers'

const searchCriteriaFromQuery = (query: string): SearchCriteria => {
  const { features, ...rest } = Object.fromEntries(new URLSearchParams(query))

  return {
    ...rest,
    features: features?.split(','),
  }
}

const DEFAULT_IMAGE_WIDTH = 300

function doesStringContainASlash(arg: string): arg is `${string}/${string}` {
  return /^[^/]+\/[^/]+$/.test(arg)
}

function formatNovasolImageURL(url: `${string}/${string}`): string {
  const jpgName = url.split('/')[1]
  const firstThreeLetters = jpgName.slice(0, 3)
  return `//image.novasol.com/pic/${DEFAULT_IMAGE_WIDTH}/${firstThreeLetters}/${jpgName}`
}

function formatUKBrandsImageURL(assetId: string): string {
  return `https://img.chooseacottage.co.uk/Property/${assetId.substring(
    0,
    3,
  )}/${DEFAULT_IMAGE_WIDTH}/${assetId}.jpg`
}

function formatPropertyImageURL(assetId: string): string {
  if (assetId && doesStringContainASlash(assetId)) {
    return formatNovasolImageURL(assetId)
  } else if (assetId) {
    return formatUKBrandsImageURL(assetId)
  }
  return 'https://img.chooseacottage.co.uk/Property/notFoundImage.png'
}

export const fetchProperties = async (
  query: string,
  options: SearchOptions & {
    currency: string
    country: AwazeCountry
    host: string
    experiments?: Record<string, string>
  },
): Promise<Omit<IPropertyCard, 'translations'>[]> => {
  if (!query) {
    return []
  }
  /**
   * TODO: Temporary solution for not having to change hundred of Contentful Destination-Pages
   * When Contentful Carousel URL's are fixed, remove the replace() from query object:
   * this will replace any query for rating above 5 with a 4-5 rating
   */
  const searchCriteria = searchCriteriaFromQuery(
    query?.replace(
      /rating=([5-9]|[1-9][0-9]+)-([6-9]|[1-9][0-9]+)/g,
      'rating=4-5',
    ),
  )

  const data = await oneSearchCall(searchCriteria, options)
  if (!data) {
    return []
  }
  const properties = data.properties || []
  const brandConfig = getBrandConfig(options.brand as SupportedBrand)

  const dateToday = new Date()
  dateToday.setHours(0, 0, 0, 0)

  const getEndDateFromStartDateDuration = (
    startDate: Date,
    duration: number,
  ): Date => {
    const safeDuration = Math.floor(duration >= 0 ? duration : 0)
    return addDays(startDate, safeDuration)
  }

  const normalizedCountry = options.country.toUpperCase()

  // instead of slice, change mfe-common
  // find image to use when no image is returned
  return properties.map((property) =>
    removeUndef({
      activeDepositScheme: property.startDate
        ? getActiveDepositScheme(
            brandConfig.deposits,
            dateToday,
            new Date(property.startDate),
            getEndDateFromStartDateDuration(
              new Date(property.startDate),
              property.lengthOfStay || 7,
            ),
            property.property?.sleeps,
            property.price,
            searchCriteria.accommodationType || 'cottages',
            normalizedCountry,
          )
        : null,
      images: [
        {
          imageURL: formatPropertyImageURL(property.images?.[0]?.assetId),
          imgAlt: property.images?.[0]?.caption,
        },
      ],
      pageURL: convertLinkToAbsoluteUrl(
        `${properFormatPropertyPageURL(
          getProductUrlPath(
            options.country,
            options.brand,
            searchCriteria.accommodationType,
          ),
          property.displayName,
          property.code,
        )}`,
        options.host,
      ),
      nights: property.lengthOfStay || 7, // &nights=x&&start=xx-xx-xxxx in the contentful query will change this value.
      hasPetsFree: !!property.petsGoFree,
      hasCallout: !!property.isCustomerChoice,
      USPs: property.USPs ?? [],
      title: decode(property.displayName),
      location: property.location,
      rhs3: property.rhs3,
      startDate: property.startDate,
      code: property.code,
      priceFrom: property.priceFrom,
      price: property.price,
      wasPrice: property.wasPrice,
      grade: property.grade ? Number(property.grade) : null,
      guests: property.property?.sleeps,
      bedrooms: property.property?.bedrooms,
      bathrooms: property.property?.bathrooms,
      pets: property.property?.pets,
      score: property.averageReviewScore,
      features: [],
      cardinality: property.cardinality,
    }),
  )
}

export const removeUndef = <T>(input: T): T => {
  if (input === null) {
    return null
  }
  if (Array.isArray(input)) {
    return input.map((v) => removeUndef(v)) as T
  }
  if (typeof input === 'object') {
    return Object.entries(input)
      .filter(([, value]) => value !== undefined)
      .reduce((acc, [key, value]) => {
        acc[key] = removeUndef(value)
        return acc
      }, {}) as T
  }
  return input
}

const holidayHomeSlugCountryMap: Record<AwazeCountry, string> = {
  AT: 'ferienhaeuser',
  DE: 'ferienhaeuser',
  DK: 'feriehuse',
  ES: 'alquiler-vacacional',
  EU: 'holidayhome',
  FR: 'location-vacances',
  GB: 'holidayhome',
  HR: 'smjestaj',
  IT: 'casa-vacanze',
  NL: 'vakantiehuis',
  NO: 'feriehus',
  PL: 'domy-wakacyjne',
  SE: 'semesterhus',
  US: 'holidayhome',
}

export const getProductUrlPath = (
  country: AwazeCountry,
  brand: Brand,
  accommodationType?: AccommodationType,
): string => {
  const defaultPath = 'cottages'

  switch (brand) {
    case 'cottages.com':
      return 'cottages'
    case 'hoseasons':
      return ['lodges', 'holiday-parks'].includes(accommodationType)
        ? 'accom'
        : accommodationType || 'cottages'
    case 'fincallorca':
    case 'jamesVillas':
    case 'novasol':
      return holidayHomeSlugCountryMap[country] ?? defaultPath
  }

  return defaultPath
}
